import { useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { Flex, Switch, Text } from '@backstage/ui';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  Account,
  AccountUser,
  AccountUserRole,
  AuthType,
  Region,
  Schedule,
  CloudAccountType,
  UserEntity,
  isAccountIdValid,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  DefaultApiResponse,
  DisplayNameAndDescriptionInput,
  ModernRightDrawer,
  useLabelsContext,
  ModernLabelsInput,
  IdInput,
  ModernSelect,
} from '@internal/backstage-plugin-devops-react';
import {
  AccountAuthTypeInput,
  AccountScanScheduleInput,
  AccountTypeInput,
} from '../Inputs';
import { useAccountsContext } from '../Providers';
import { normalizeEntityRef } from '../SettingsPages/useAccountSettings';
import { AccountApi, accountApiRef } from '../../api';

interface EditAccountDrawerProps {
  isOpen: boolean;
  project: Entity;
  onClose: () => void;
}

export function EditAccountDrawer(props: Readonly<EditAccountDrawerProps>) {
  const { isOpen, project, onClose } = props;

  const { selectedAccount, unselectAccount, createAccount, editAccount } =
    useAccountsContext();

  const accountApi = useApi<AccountApi>(accountApiRef);

  const {
    labels,
    isKeyInvalidFormat,
    isDuplicated,
    isValueEmpty,
    loadLabels,
    resetLabels,
  } = useLabelsContext();

  const [accountId, setAccountId] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [accountType, setAccountType] = useState<string>(CloudAccountType.AWS);
  const [authType, setAuthType] = useState<AuthType>(AuthType.AWS_ACCESS_KEY);
  const [scanSchedule, setScanSchedule] = useState<Schedule | undefined>(
    undefined,
  );
  const [adminUser, setAdminUser] = useState<string>('');
  const [resetAdmin, setResetAdmin] = useState<boolean>(false);
  const [users, setUsers] = useState<UserEntity[]>([]);

  const [isInitialInput, setIsInitialInput] = useState<boolean>(true);
  const [idInvalid, setIdInvalid] = useState<boolean>(false);
  const [nameInvalid, setNameInvalid] = useState<boolean>(false);

  const isCreating = selectedAccount === undefined;

  const currentUsers: AccountUser[] = selectedAccount?.spec?.users ?? [];
  const hasAdmin = currentUsers.some(
    user => user.Role === AccountUserRole.ADMIN,
  );
  const currentAdminNames = currentUsers
    .filter(user => user.Role === AccountUserRole.ADMIN)
    .map(user => normalizeEntityRef(user.UserRef));

  useEffect(() => {
    if (!isOpen) return;

    const applyUsers = (response: DefaultApiResponse) => {
      setUsers(
        response.succeeded
          ? ((response.body?.items ?? []) as UserEntity[])
          : [],
      );
    };

    if (isCreating) {
      accountApi
        .getUsers()
        .then(applyUsers)
        .catch(() => setUsers([]));
      return;
    }

    // Editing an account that already has admins: the role can only be
    // reset, no user list is needed.
    if (hasAdmin) {
      setUsers([]);
      return;
    }

    // Editing an account without admins: offer only members of the
    // account owner group, since they are the audience of the account.
    const owner = project.spec!.owner as string;
    const ownerGroupRef = owner.includes(':')
      ? owner
      : `group:default/${owner.toLowerCase()}`;

    accountApi
      .getUsers(ownerGroupRef)
      .then(applyUsers)
      .catch(() => setUsers([]));
  }, [accountApi, isOpen, isCreating, hasAdmin, project]);

  useEffect(() => {
    if (!isOpen) return;

    setIsInitialInput(true);

    if (!selectedAccount) return;

    setAccountId(selectedAccount.metadata.name);
    setDisplayName(selectedAccount.metadata.title ?? '');
    setDescription(selectedAccount.metadata.description ?? '');
    setAccountType(selectedAccount.spec.type);
    setAuthType(selectedAccount.spec.authType ?? AuthType.AWS_ACCESS_KEY);
    setScanSchedule(selectedAccount.spec.scanSchedule);
    loadLabels(selectedAccount.metadata.labels);
  }, [selectedAccount, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIdInvalid(!isAccountIdValid(accountId, accountType as CloudAccountType));
  }, [accountId, accountType]);

  useEffect(() => {
    setNameInvalid(displayName.length < 3);
  }, [displayName]);

  const clean = () => {
    setAccountId('');
    setDisplayName('');
    setDescription('');
    setAccountType(CloudAccountType.AWS);
    setAuthType(AuthType.AWS_ACCESS_KEY);
    setScanSchedule(undefined);
    setAdminUser('');
    setResetAdmin(false);
    resetLabels();
  };

  const handleAccountTypeChange = (newAccountType: string) => {
    setAccountType(newAccountType);
    if (newAccountType === CloudAccountType.AWS) {
      setAuthType(AuthType.AWS_ACCESS_KEY);
    } else {
      setAuthType(AuthType.LOAD_AZURE);
    }
    setScanSchedule(undefined);
  };

  const handleClose = () => {
    clean();
    unselectAccount();

    onClose();
  };

  const handleSubmit = async () => {
    if (
      idInvalid ||
      nameInvalid ||
      isKeyInvalidFormat ||
      isDuplicated ||
      isValueEmpty
    ) {
      if (isInitialInput) {
        setIsInitialInput(false);
      }
      return;
    }

    const resultUsers: AccountUser[] = isCreating
      ? []
      : currentUsers.map(user => ({ ...user }));

    if (hasAdmin && resetAdmin) {
      resultUsers.forEach(user => {
        if (user.Role === AccountUserRole.ADMIN) {
          user.Role = AccountUserRole.CONTRIBUTOR;
        }
      });
    }

    if (adminUser) {
      resultUsers.push({
        UserRef: `user:default/${adminUser}`,
        Role: AccountUserRole.ADMIN,
      });
    }

    const resultAccount: Account = {
      AccountId: selectedAccount?.metadata?.name ?? accountId,
      AccountName: selectedAccount?.metadata.title ?? '',
      Title: displayName,
      Type: accountType as CloudAccountType,
      Description: description,
      Labels: labels,
      BackstageOwner: project.spec!.owner as string,
      Project: project.metadata.name,
      Errors: selectedAccount?.spec?.errors ?? [],
      MutedErrors: selectedAccount?.spec?.mutedErrors ?? [],
      Regions: (selectedAccount?.spec?.regions as Region[]) ?? [],
      CatalogFormatVersion: selectedAccount?.spec?.catalogFormatVersion ?? 0,
      InventoryFormatVersion:
        selectedAccount?.spec?.inventoryFormatVersion ?? 0,
      ReportFormatVersion: selectedAccount?.spec?.reportFormatVersion ?? 0,
      AuthType: authType,
      LastScanTime: selectedAccount?.spec?.lastScanTime ?? undefined,
      LastSyncTime: selectedAccount?.spec?.lastSyncTime ?? undefined,
      ScanSchedule: scanSchedule,
      Users: resultUsers,
    };

    if (selectedAccount === undefined) {
      createAccount(resultAccount);
    } else {
      editAccount(resultAccount);
    }

    clean();
    onClose();
  };

  let invalidIdHelperText = '';
  switch (accountType) {
    case CloudAccountType.AWS:
      invalidIdHelperText = 'AWS Account ID is 12-digit number.';
      break;
    case CloudAccountType.AZURE:
      invalidIdHelperText = 'AZURE';
      break;
    default:
      break;
  }

  return (
    <ModernRightDrawer
      isOpen={isOpen}
      title={`${selectedAccount === undefined ? 'Create' : 'Edit'} Account`}
      submitButtonText="Confirm"
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <AccountTypeInput
        accountType={accountType}
        setAccountType={handleAccountTypeChange}
        isInvalid={accountType.length <= 0 && !isInitialInput}
        disabled={selectedAccount !== undefined}
      />
      <IdInput
        entityKind="Cloud Account"
        id={accountId}
        setId={setAccountId}
        isInvalid={idInvalid && !isInitialInput}
        isDisabled={selectedAccount !== undefined}
        description={invalidIdHelperText}
      />
      <DisplayNameAndDescriptionInput
        entityKind="Account"
        displayName={displayName}
        setDisplayName={setDisplayName}
        isNameInvalid={nameInvalid && !isInitialInput}
        nameDescription="Account's name should be at least 3 characters long."
        description={description}
        setDescription={setDescription}
      />
      <AccountAuthTypeInput
        accountType={accountType as CloudAccountType}
        accountAuthType={authType}
        setAccountAuthType={setAuthType}
        isInvalid={authType.length <= 0 && !isInitialInput}
      />
      <AccountScanScheduleInput
        accountScanSchedule={scanSchedule}
        setAccountScanSchedule={setScanSchedule}
        hidden={authType !== AuthType.AWS_INSTANCE_PROFILE}
        isInvalid={scanSchedule === undefined && !isInitialInput}
      />
      {isCreating && (
        <ModernSelect
          label="Account Admin"
          options={users.map(user => ({
            label: user.spec.profile?.displayName ?? user.metadata.name,
            value: user.metadata.name,
          }))}
          value={adminUser}
          onChange={value => setAdminUser(value?.toString() ?? '')}
          emptyOptionLabel="No admin"
          description="The selected user gets the Admin role for this account and is added to the project owner group."
        />
      )}
      {!isCreating && !hasAdmin && (
        <ModernSelect
          label="Account Admin"
          options={users.map(user => ({
            label: user.spec.profile?.displayName ?? user.metadata.name,
            value: user.metadata.name,
          }))}
          value={adminUser}
          onChange={value => setAdminUser(value?.toString() ?? '')}
          emptyOptionLabel="No admin"
          description="The selected user gets the Admin role for this account."
        />
      )}
      {!isCreating && hasAdmin && (
        <Flex direction="column" gap="1">
          <Switch
            isSelected={resetAdmin}
            onChange={checked => setResetAdmin(checked)}
          >
            Reset Admin
          </Switch>
          <Text variant="body-x-small">
            {currentAdminNames.length > 0
              ? `Sets the Contributor role for all account admins: ${currentAdminNames.join(', ')}.`
              : 'Sets the Contributor role for all account admins.'}
          </Text>
        </Flex>
      )}
      <ModernLabelsInput isInitialInput={isInitialInput} />
    </ModernRightDrawer>
  );
}
